import React from 'react';
import { Typography, Card, Flex, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { purifyHtml } from '~library/purifyHtml';

import { CustomTooltip } from '../Tooltip';

type ChartCardProps = {
  title: string;
  isLoading: boolean;
  description?: string;
  descriptionType?: 'paragraph' | 'tooltip';
  errorFallback?: React.ReactNode;
};

type ChartCardHeaderProps = Pick<ChartCardProps, 'title' | 'description' | 'descriptionType'>;

function ChartCardDescriptionParagraph({ description }: Pick<ChartCardProps, 'description'>) {
  const { token } = theme.useToken();

  if (!description) {
    return null;
  }

  return (
    <Typography.Paragraph
      type="secondary"
      style={{
        fontSize: token.fontSizeSM,
        lineHeight: token.lineHeightSM,
        marginBottom: 0,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        fontWeight: 'normal',
      }}
    >
      <span dangerouslySetInnerHTML={{ __html: purifyHtml(description) }} />
    </Typography.Paragraph>
  );
}

function ChartCardHeader({ title, description, descriptionType }: ChartCardHeaderProps) {
  const { token } = theme.useToken();
  const showDescriptionParagraph = descriptionType === 'paragraph' && Boolean(description);
  const showDescriptionTooltip = descriptionType === 'tooltip' && Boolean(description);

  return (
    <Flex vertical gap={token.marginXXS}>
      <Flex align="center" gap={token.marginXXS}>
        <Typography.Text strong>{title}</Typography.Text>
        {showDescriptionTooltip && (
          <CustomTooltip placement="top" message={description}>
            <InfoCircleOutlined style={{ color: token.colorIcon }} />
          </CustomTooltip>
        )}
      </Flex>
      {showDescriptionParagraph && <ChartCardDescriptionParagraph description={description} />}
    </Flex>
  );
}

function ChartCard({
  title,
  isLoading,
  description,
  descriptionType,
  errorFallback,
  children,
}: React.PropsWithChildren<ChartCardProps>) {
  const { token } = theme.useToken();
  const hasDescriptionParagraph = descriptionType === 'paragraph' && Boolean(description);

  return (
    <Card
      title={<ChartCardHeader title={title} description={description} descriptionType={descriptionType} />}
      styles={{
        header: hasDescriptionParagraph ? { paddingBlock: token.paddingMD } : undefined,
        title: hasDescriptionParagraph
          ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }
          : undefined,
        body: { paddingTop: token.padding, height: '100%' },
      }}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow:
          '0px 1px 2px 0px rgba(0, 0, 0, 0.03), 0px 1px 6px -1px rgba(0, 0, 0, 0.02), 0px 2px 4px 0px rgba(0, 0, 0, 0.02)',
      }}
      loading={isLoading}
    >
      {errorFallback ?? children}
    </Card>
  );
}

function ChartCardContent({ children }: React.PropsWithChildren) {
  return (
    <Flex justify="space-between" vertical style={{ height: '100%' }}>
      {children}
    </Flex>
  );
}

function ChartCardChartLayout({ children }: React.PropsWithChildren) {
  return <div style={{ minHeight: 220 }}>{children}</div>;
}

ChartCard.Content = ChartCardContent;
ChartCard.ChartLayout = ChartCardChartLayout;

export default ChartCard;
